/** Shipment Inventory Lines Admin API (`/v1/shipments/admin`) — camelCase. */

export type ShipmentLineType = "PACK" | "ITEM";

export type ShipmentLineStatus =
  | "RESERVED"
  | "DISPATCHED"
  | "REMOVED";

export type ShipmentLineErrorCode =
  | "SHIPMENT_NOT_FOUND"
  | "LINE_NOT_FOUND"
  | "LINE_NOT_REMOVABLE"
  | "PACK_LINE_NOT_REMOVABLE"
  | "PACK_NOT_ON_SHIPMENT"
  | "SHIPMENT_TYPE_NOT_ALLOWED"
  | "SHIPMENT_STATUS_NOT_ALLOWED"
  | "CANNOT_DISPATCH_SHORT"
  | "UNRESOLVABLE_SKU";

export interface ShipmentLineStockBalance {
  clientSku: string;
  onHand: number;
  reserved: number;
  free: number;
}

export interface ShipmentLineReservedDelta {
  clientSku: string;
  productId?: number;
  reservedDelta: number;
  lineId?: number;
}

export interface ShipmentLineRestoredDelta {
  clientSku: string;
  onHandDelta: number;
}

export interface ShipmentLineSkuComponent {
  clientSku: string;
  productId: number;
  qty: number;
}

export interface ShipmentLineSkuRow {
  lineId: number;
  clientSku: string;
  productId: number;
  variantId: number;
  qty: number;
}

// ── Requests ────────────────────────────────────────────────────────────────

export interface AddShipmentPackLineRequest {
  packId: number;
  qty: number;
}

export interface AddShipmentItemLineRequest {
  clientSku: string;
  qty: number;
}

export interface BulkAddShipmentPackLineRequest {
  shipmentIds: number[];
  packId: number;
  qty: number;
}

export interface BulkAddShipmentItemLineRequest {
  shipmentIds: number[];
  clientSku: string;
  qty: number;
}

export interface ListShipmentLinesParams {
  includeRemoved?: boolean;
}

export interface DispatchShipmentLinesRequest {
  note?: string;
}

// ── Responses ───────────────────────────────────────────────────────────────

export interface AddShipmentPackLineResult {
  shipmentId: number;
  lineType: "PACK";
  packId: number;
  qty: number;
  status: ShipmentLineStatus;
  lines: ShipmentLineSkuRow[];
  reserved: ShipmentLineReservedDelta[];
  stock: ShipmentLineStockBalance[];
}

export interface AddShipmentItemLineResult {
  lineId: number;
  shipmentId: number;
  lineType: "ITEM";
  clientSku: string;
  productId: number;
  variantId: number;
  qty: number;
  status: ShipmentLineStatus;
  stock: ShipmentLineStockBalance[];
}

export interface BulkAddShipmentPackReservedDelta {
  clientSku: string;
  productId: number;
  reservedDeltaPerShipment: number;
  reservedDeltaTotal: number;
}

export interface BulkAddShipmentPackLineResult {
  lineType: "PACK";
  packId: number;
  qty: number;
  status: ShipmentLineStatus;
  shipmentCount: number;
  linesCreated: number;
  inventoryId: number;
  reserved: BulkAddShipmentPackReservedDelta[];
  stock: ShipmentLineStockBalance[];
}

export interface BulkAddShipmentItemLineResult {
  lineType: "ITEM";
  clientSku: string;
  productId: number;
  variantId: number;
  qty: number;
  status: ShipmentLineStatus;
  shipmentCount: number;
  linesCreated: number;
  inventoryId: number;
  reservedDeltaTotal: number;
  stock: ShipmentLineStockBalance[];
}

export interface ShipmentInventoryLine {
  lineId: number;
  lineType: ShipmentLineType;
  packId?: number;
  productId: number;
  variantId: number;
  clientSku: string;
  qty: number;
  status: ShipmentLineStatus;
  components?: ShipmentLineSkuComponent[];
}

export interface ListShipmentLinesResult {
  shipmentId: number;
  lines: ShipmentInventoryLine[];
}

export interface RemoveShipmentLineResult {
  lineId: number;
  status: "REMOVED";
  declaredValue?: number;
  released: ShipmentLineReservedDelta[];
  restored?: ShipmentLineRestoredDelta[];
  stock: ShipmentLineStockBalance[];
}

export interface RemoveShipmentPackLinesResult {
  shipmentId: number;
  packId: number;
  status: "REMOVED";
  declaredValue?: number;
  removedLineIds: number[];
  released: ShipmentLineReservedDelta[];
  restored?: ShipmentLineRestoredDelta[];
  stock: ShipmentLineStockBalance[];
}

export interface DispatchedShipmentLine {
  lineId: number;
  lineType: ShipmentLineType;
  packId?: number;
  qty: number;
}

export interface DispatchShipmentLinesResult {
  shipmentId: number;
  dispatched: DispatchedShipmentLine[];
  stock: ShipmentLineStockBalance[];
}

export interface DispatchShortfall {
  clientSku: string;
  onHand: number;
  required: number;
}

export interface DispatchShortfallDetails {
  shortfalls: DispatchShortfall[];
}

// ── Bulk upload lines (CSV) — `POST /lines/{pack|item}/bulk-upload` ────────

/** A row left out of the job because its shipment is invalid. */
export interface ShipmentLinesUploadSkippedRow {
  rowNumber: number;
  shipmentId: number;
  reason: string;
}

export interface ShipmentLinesUploadResult {
  jobId: number;
  totalRows: number;
  /** Rows of valid shipments that the job will write. */
  queuedRows: number;
  /** Valid shipments whose lines the file replaces. */
  shipmentCount: number;
  skippedCount: number;
  skipped: ShipmentLinesUploadSkippedRow[];
  inventoryId: number;
  orderId: number | null;
  /** Valid shipments by the status their new lines get (New / Ready To Ship → RESERVED, Shipped → DISPATCHED). */
  shipmentsByLineStatus: Partial<
    Record<Extract<ShipmentLineStatus, "RESERVED" | "DISPATCHED">, number>
  >;
}

export interface ShipmentLinesUploadJobStatus {
  jobId: number;
  lineType: ShipmentLineType;
  /** 0–99 while running, 100 when finished. */
  progress: number;
  finished: boolean;
  success: boolean;
  summary: string | null;
  startTime: string | null;
  endTime: string | null;
}

interface ShipmentLinesUploadErrorDetails {
  rowErrors?: { rowNumber: number; message: string }[];
  missingColumns?: string[];
  maxRows?: number;
  rowCount?: number;
  validationFailures?: {
    rowNumber: number;
    shipmentId: number;
    reason: string;
  }[];
  inventories?: {
    inventoryId: number;
    rowCount: number;
    sampleRowNumbers: number[];
  }[];
  orders?: {
    orderId: number | null;
    rowCount: number;
    sampleRowNumbers: number[];
  }[];
  shortfalls?: DispatchShortfall[];
  /** `LINES_UPLOAD_IN_PROGRESS`: the job already running for this order. */
  runningJob?: { jobId: number } | null;
}

/** Upload failure; `runningJobId` is set when another upload for the order is still running. */
export class ShipmentLinesUploadError extends Error {
  runningJobId: number | null;

  constructor(message: string, runningJobId: number | null = null) {
    super(message);
    this.name = "ShipmentLinesUploadError";
    this.runningJobId = runningJobId;
  }
}

/** Reads `details.runningJob.jobId` from a `409 LINES_UPLOAD_IN_PROGRESS` response. */
export const getShipmentLinesUploadRunningJobId = (
  error: unknown,
): number | null => {
  try {
    const jobId = (
      error as {
        response?: {
          data?: { result?: { details?: ShipmentLinesUploadErrorDetails } };
        };
      }
    )?.response?.data?.result?.details?.runningJob?.jobId;
    return typeof jobId === "number" && jobId > 0 ? jobId : null;
  } catch {
    return null;
  }
};

const MAX_UPLOAD_ERROR_LINES = 10;

const capLines = (lines: string[]): string[] =>
  lines.length > MAX_UPLOAD_ERROR_LINES
    ? [
        ...lines.slice(0, MAX_UPLOAD_ERROR_LINES),
        `+${lines.length - MAX_UPLOAD_ERROR_LINES} more`,
      ]
    : lines;

const formatSampleRows = (rows: number[]): string =>
  rows.length > 0 ? ` (e.g. row ${rows.join(", ")})` : "";

/**
 * Turns a failed lines CSV upload into readable text: the server message plus
 * one line per failing row / group from `result.details`.
 */
export const getShipmentLinesUploadErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  try {
    const data = (
      error as {
        response?: {
          data?: {
            message?: string;
            result?: { details?: ShipmentLinesUploadErrorDetails };
          };
        };
      }
    )?.response?.data;
    const message =
      data?.message ||
      (error instanceof Error ? error.message : "") ||
      fallbackMessage;
    const details = data?.result?.details;
    if (!details) return message;

    const lines: string[] = [];
    if (details.missingColumns?.length) {
      lines.push(`Missing column(s): ${details.missingColumns.join(", ")}`);
    }
    if (details.maxRows != null && details.rowCount != null) {
      lines.push(
        `CSV has ${details.rowCount} rows. The limit is ${details.maxRows}.`,
      );
    }
    details.rowErrors?.forEach((row) =>
      lines.push(`Row ${row.rowNumber}: ${row.message}`),
    );
    details.validationFailures?.forEach((row) =>
      lines.push(
        `Row ${row.rowNumber}, shipment ${row.shipmentId}: ${row.reason}`,
      ),
    );
    details.inventories?.forEach((group) =>
      lines.push(
        `Inventory ${group.inventoryId}: ${group.rowCount} row(s)${formatSampleRows(group.sampleRowNumbers)}`,
      ),
    );
    details.orders?.forEach((group) =>
      lines.push(
        `${group.orderId == null ? "No order" : `Order ${group.orderId}`}: ${group.rowCount} row(s)${formatSampleRows(group.sampleRowNumbers)}`,
      ),
    );
    details.shortfalls?.forEach((row) =>
      lines.push(
        `${row.clientSku}: on hand ${row.onHand}, needs ${row.required}`,
      ),
    );

    return lines.length > 0
      ? [message, ...capLines(lines)].join("\n")
      : message;
  } catch {
    return fallbackMessage;
  }
};
